/**
 * Pick your name: the one-time name choice after a first email sign-in (docs/accounts/PLAN.md,
 * addendum). An email account starts out wearing its guest's `Guest-XXXX`, and this is where that
 * stops. Discord accounts keep their Discord name and never see it.
 *
 * Two ways in:
 *  - `NamePickHost` opens it by itself, once per boot, the first time the lobby is on screen — after
 *    the "Signed in with email" toast, and never over a live hand. "Later" really is later: nothing
 *    is stored, so it comes back on the next visit until a name is picked.
 *  - the Account sheet's "Pick your name" button, for a player who said Later and changed their mind.
 *
 * The shared `validateDisplayName` runs as the player types, so the server's 400 is a backstop and
 * not the usual way to learn a name is too short. A 409 means a name was already picked (another tab
 * got there first): the sheet closes without a word and the session is re-read so the new name shows.
 */
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { DISPLAY_NAME_MAX, validateDisplayName } from '@spades/shared';
import { refreshSession, writeStoredToken } from '../../bridge/identity';
import { type NameFailure, canPickName, nameFailureText, submitDisplayName } from '../../net/accountApi';
import { store, useStore } from '../../store/store';
import { getRuntime } from '../runtime';
import { Button, Sheet, toast } from '../ui';

export interface PickNameSheetProps {
  open: boolean;
  onClose: () => void;
  /** Design lab: start with this text in the box. */
  initialName?: string;
  /** Design lab: start showing this server refusal. */
  initialError?: NameFailure;
}

/**
 * Adopt a freshly minted session: stored token, store, and the live driver, whose next join would
 * otherwise still send the JWT with the old name in it. Duck-typed on purpose — importing the
 * Colyseus driver here would drag its chunk onto the shell's path.
 */
function adoptSession(token: string, name?: string): void {
  writeStoredToken(token);
  const s = store.getState();
  const me = s.session.me;
  s.setSession({ token, me: me && name ? { ...me, name } : me });
  (getRuntime()?.driver as { setToken?: (t: string) => void } | undefined)?.setToken?.(token);
}

export function PickNameSheet({ open, onClose, initialName = '', initialError }: PickNameSheetProps) {
  const [value, setValue] = useState(initialName);
  const [touched, setTouched] = useState(!!initialName);
  const [serverError, setServerError] = useState<NameFailure | null>(initialError ?? null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setValue(initialName);
    setTouched(!!initialName);
    setServerError(initialError ?? null);
    // After the Sheet's own focus pass, which runs later in the same commit and lands on Close.
    const t = window.setTimeout(() => input.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, initialName, initialError]);

  const check = validateDisplayName(value);
  // A short name is only an error once the player has tried to send it; mid-word, "A" is not wrong.
  const liveError = !check.ok && value.trim() !== '' && (check.reason !== 'length' || touched) ? nameFailureText(check.reason) : null;
  const error = liveError ?? (serverError ? nameFailureText(serverError) : null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!check.ok || busy) return;
    setBusy(true);
    const res = await submitDisplayName(check.name);
    setBusy(false);
    if (res.ok) {
      adoptSession(res.token, res.name);
      toast(`You're ${res.name} now.`, 'success', 3600);
      onClose();
      return;
    }
    if (res.reason === 'taken') {
      onClose();
      void refreshSession()
        .then(({ token, user }) => adoptSession(token, user.name))
        .catch(() => {});
      return;
    }
    setServerError(res.reason);
  };

  return (
    <Sheet open={open} title="Pick your name" onClose={onClose} testId="pick-name">
      <form className="stack account__form" onSubmit={(e) => void onSubmit(e)} noValidate>
        <p className="muted" style={{ margin: 0 }}>
          This is how other players see you at the table.
        </p>
        <label className="small muted" htmlFor="pick-name-input">
          Name
        </label>
        <input
          ref={input}
          id="pick-name-input"
          className="input"
          type="text"
          autoComplete="nickname"
          autoCapitalize="words"
          spellCheck={false}
          maxLength={DISPLAY_NAME_MAX}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setServerError(null);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'pick-name-error' : undefined}
          data-testid="pick-name-input"
        />
        {error && (
          <p id="pick-name-error" className="small account__error" role="alert" data-testid="pick-name-error">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" block disabled={busy || !check.ok} testId="pick-name-save">
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="ghost" block onClick={onClose} testId="pick-name-later">
          Later
        </Button>
      </form>
    </Sheet>
  );
}

/** Once per page load, however many times the lobby is re-entered. */
let offeredThisBoot = false;

/** Tests only. */
export function resetNamePickOffer(): void {
  offeredThisBoot = false;
}

/**
 * Opens the sheet by itself for an email account that has never picked. Web only (Discord and
 * guests never match `canPickName`), and only on the lobby, so a reload mid-hand is not interrupted.
 */
export function NamePickHost() {
  const platform = useStore((s) => s.session.platform);
  const me = useStore((s) => s.session.me);
  const screen = useStore((s) => s.presentation.screen);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (offeredThisBoot || platform !== 'web' || screen !== 'lobby' || !canPickName(me)) return;
    offeredThisBoot = true;
    setOpen(true);
  }, [platform, screen, me]);

  return <PickNameSheet open={open} onClose={() => setOpen(false)} />;
}
